import {
    Box,
    Button,
    Card,
    Center,
    Divider,
    Flex,
    Group,
    Loader,
    LoadingOverlay,
    Modal,
    Stack,
    Text,
    TextInput,
    ThemeIcon,
    Title,
    UnstyledButton,
} from '@mantine/core'
import {
    Check,
    Package,
    Phone,
    Plus,
} from 'lucide-react'
import { Controller } from 'react-hook-form'

import { Form } from '@/components/atoms/Form'
import ModalHeader from '@/modules/Cart/shared/ModalHeader/ModalHeader'
import { WithViewModelProps } from '@/shared/types/components'

import { useViewModel } from './CustomerShipping.vm'
import Footer from '@/modules/Cart/Footer'
import CreateShippingAddressModal from './CreateShippingAddressModal'

export const CustomerShippingTitle = () => {
    const metadata = {
        title: 'Adresse de livraison',
        description:
            'Veuillez renseigner une adresse de livraison pour votre commande.',
    }
    return <ModalHeader {...metadata} />
}

export const CustomerShipping = () => {
    const viewModel = useViewModel()
    return <CustomerShippingView viewModel={viewModel} />
}

const CustomerShippingView = ({
    viewModel,
}: WithViewModelProps<typeof useViewModel>) => {
    if (viewModel.isLoading) {
        return (
            <Center className="h-full">
                <Loader size="xs" color="gray" />
            </Center>
        )
    }

    if (!viewModel.shippingAddresses.length) {
        return (
            <>
                <CreateShippingAddressModal viewModel={viewModel} />
                <Card className="h-full p-4 rounded">
                    <Stack
                        className="items-center justify-center h-full max-w-xs mx-auto text-center"
                        spacing={'sm'}
                    >
                        <ThemeIcon
                            size={48}
                            radius={9999}
                            variant="light"
                            color="gray"
                        >
                            <Package />
                        </ThemeIcon>
                        <Title order={5}>Aucune adresse de livraison</Title>
                        <Text size="sm" c="gray.5">
                            Vous pouvez ajouter une nouvelle adresse en cliquant{' '}
                            <UnstyledButton
                                onClick={viewModel.onCreate}
                                className="text-sm text-blue-400 underline underline-offset-2"
                            >
                                ici
                            </UnstyledButton>
                        </Text>
                    </Stack>
                </Card>
            </>
        )
    }

    return (
        <Box>
            <CreateShippingAddressModal viewModel={viewModel} />
            {!viewModel.hasReachedShippingAddressLimit && (
                <Box className="flex items-end w-full p-4">
                    <Button
                        onClick={viewModel.onCreate}
                        size="xs"
                        variant="light"
                        color="orange"
                        ml="auto"
                        leftIcon={<Plus className="w-4 h-4" />}
                    >
                        Ajouter une adresse
                    </Button>
                </Box>
            )}

            <Stack w="100%" className="p-4">
                {viewModel.shippingAddresses.map((shippingAddress) => (
                    <Card
                        withBorder
                        key={shippingAddress.id}
                        className="flex justify-between w-full p-4 transition-all duration-300 ease-in-out border-dashed rounded"
                        bg={
                            viewModel.selectedShippingAddress?.id ===
                            shippingAddress.id
                                ? 'gray.2'
                                : 'white'
                        }
                    >
                        <Group>
                            <Box>
                                <Title order={6} c="gray.7">
                                    {shippingAddress.first_name}{' '}
                                    <span className="uppercase">
                                        {shippingAddress.last_name}
                                    </span>
                                </Title>
                                <Text size="sm" c="gray.5">
                                    {shippingAddress.address_1}
                                </Text>
                                <Text size="sm" c="gray.5">
                                    {shippingAddress.address_2}
                                </Text>
                                <Group c="gray.5" spacing={2}>
                                    <Text size="sm">
                                        {shippingAddress.city},
                                    </Text>
                                    <Text size="sm">
                                        {shippingAddress.postal_code}
                                    </Text>
                                </Group>
                            </Box>
                        </Group>
                        <Stack>
                            <Button
                                size="xs"
                                fullWidth
                                variant="outline"
                                color="gray"
                                disabled={
                                    viewModel.selectedShippingAddress?.id ===
                                    shippingAddress.id
                                }
                                onClick={() =>
                                    viewModel.onSelectAddress(
                                        shippingAddress.id,
                                    )
                                }
                            >
                                {viewModel.selectedShippingAddress?.id ===
                                shippingAddress.id ? (
                                    <Check className="w-4 h-4" />
                                ) : (
                                    'Sélectionner'
                                )}
                            </Button>
                            <Button
                                fullWidth
                                color="var(--primary-light)"
                                size="xs"
                                variant="outline"
                                onClick={() =>
                                    viewModel.onEdit(shippingAddress.id)
                                }
                            >
                                Modifier
                            </Button>
                        </Stack>
                    </Card>
                ))}
            </Stack>

            <Footer>
                <Button
                    onClick={viewModel.onNext}
                    disabled={
                        viewModel.selectedShippingAddress === null ||
                        viewModel.isLoading ||
                        viewModel.form.formState.isSubmitting
                    }
                    loading={
                        viewModel.isLoading ||
                        viewModel.form.formState.isSubmitting
                    }
                    className="ml-auto"
                >
                    Suivant
                </Button>
            </Footer>
        </Box>
    )
}
